import { z } from "zod"
import { supabase } from "../db"
import { createEmbedding } from "../search/documents"

/**
 * HOW SEARCH SCORES A MATCH
 *
 * A query like "housing deposit rules" is split into words (queryTerms),
 * dropping filler words that don't help match anything ("the", "with", "how"
 * — the STOPWORDS list) so what's left is "housing", "deposit", "rules".
 *
 * Each candidate record is flattened to plain searchable text (searchableText
 * pulls every string out of it, ignoring field names) and lowercased into one
 * blob called "hay" — as in haystack, the text a term gets searched for in.
 * scoreRecord then checks each query term against that hay: found in the
 * title is worth more than found in the body, and matching a whole word
 * ("aid" alone) beats matching inside another word ("aid" inside "said").
 * Terms found are summed and scaled by how many of the query's words showed
 * up at all, so a record matching every word ranks above one matching only
 * one word repeatedly.
 */

/**
 * THE TOOL SURFACE, IN / OUT
 *
 * One tool, one shape of call: { action, path, query, depth } in, a plain
 * text string out. Every action shares those same four fields; each just
 * ignores the ones it doesn't need.
 *
 *   list(path)            -> a directory listing. "" lists the 3 top areas
 *                            (articles/services/site); a path like
 *                            "articles/category:01H..." lists that node's
 *                            children. Each line is tagged [dir] (has
 *                            children, list it next) or [doc] (has content,
 *                            read it next), with a "path:" you copy for the
 *                            next call. query/depth are ignored.
 *
 *   tree(path, depth)     -> the same listing, expanded several levels deep
 *                            in one call instead of one list() per level.
 *                            depth (1-5, default 2) caps how far it goes.
 *                            query is ignored.
 *
 *   read(path)            -> the full content of one [doc] node — an
 *                            article's body, a service's details, a site
 *                            page block. query/depth are ignored.
 *
 *   search(query, path)   -> ranked matches: keyword scoring (above), merged
 *                            with meaning matches when the team has turned
 *                            meaning search on (see HYBRID SEARCH below).
 *                            path is optional here and narrows the search to
 *                            one subtree instead of the whole CMS; depth is
 *                            ignored.
 *
 * path is always a string built from earlier output, never composed by hand:
 * "" is the root, and each segment after that is "kind:id" (e.g.
 * "category:01HXYZ"), copied straight from a "path:" line in a prior result.
 */

/** The three content areas, each the root of its own tree. */
const AREAS = ["articles", "services", "site"] as const
type Area = (typeof AREAS)[number]

const AREA_LABEL: Record<Area, string> = {
  articles: "Articles",
  services: "Service Map",
  site: "Site Config",
}

const FETCH_CEILING = 500

/** Depth cap for `tree`, so a deep KB cannot return the entire corpus. */
const MAX_TREE_DEPTH = 5

declare global {
  interface CMSSearchWorker extends AIWorker {
    fields: {
      input: NodeIO
      output: NodeIO
      textOutput: NodeIO
      references: NodeIO
      tool: NodeIO
      maxResults: NodeIO
      locale: NodeIO
      condition: NodeIO
    }
    parameters: {
      searchArticles?: boolean
      searchServices?: boolean
      searchSites?: boolean
      maxResults?: number
      /** Preferred locale for translated fields; falls back to any available. */
      locale?: string
      toolDescription?: string
      /** "keyword" skips meaning search even when the team has it on. */
      searchMode?: "hybrid" | "keyword"
    }
  }
}

// ── Paths ────────────────────────────────────────────────────────────────────

interface CmsPath {
  area?: Area
  segments: { kind: string; id: string }[]
}

function parsePath(raw: string | null | undefined): CmsPath | { error: string } {
  const clean = String(raw ?? "").trim().replace(/^\/+|\/+$/g, "")
  if (!clean) return { segments: [] }

  const parts = clean.split("/").filter(Boolean)
  const area = parts[0] as Area
  if (!AREAS.includes(area)) {
    return { error: `"${parts[0]}" is not a CMS area. Use one of: ${AREAS.join(", ")}.` }
  }

  const segments: { kind: string; id: string }[] = []
  for (const part of parts.slice(1)) {
    const idx = part.indexOf(":")
    if (idx < 1) {
      return {
        error: `Path segment "${part}" is malformed. Each segment after the area is `
          + `"kind:id", e.g. "category:01HXYZ" — copy the path from a list result rather `
          + `than composing one by hand.`,
      }
    }
    segments.push({ kind: part.slice(0, idx), id: part.slice(idx + 1) })
  }
  return { area, segments }
}

const pathOf = (area: Area, ...segments: { kind: string; id: string }[]) =>
  [area, ...segments.map(s => `${s.kind}:${s.id}`)].join("/")

// ── Public site links ────────────────────────────────────────────────────────

/*
 * The public Signpost sites route by a numeric id, not the Smart CMS ULID: a record's legacy `oldid` when it has one,
 * otherwise a hash of its ULID. MUST match publicId() in signpost-monorepo lib/smartcms/map.ts, or links 404.
 * Knowledge-base tables have no `oldid`, so articles always use the hash
 */
export function publicId(id: string, oldid?: number | null): number {
  if (oldid != null) return Number(oldid)
  let h = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return 1_000_000_000 + (h % 1_000_000_000)
}

// Site-relative links, without a locale: the sites' routes take the locale as optional and keep the reader's own.
// The site's chat matches "/services/<id>" in references to show service cards
export const articleUrl = (a: { id: string }) => `/articles/${publicId(a.id)}`
export const serviceUrl = (sv: { id: string; oldid?: number | null }) => `/services/${publicId(sv.id, sv.oldid)}`

// ── Entries ──────────────────────────────────────────────────────────────────

interface Entry {
  path: string
  /** Where the record lives on the public site, for citing. Only articles and services have pages */
  url?: string
  kind: string
  title: string
  childCount?: number
  /** True when this holds readable body content. */
  readable?: boolean
  /** One-line context: status, locale coverage, address. */
  detail?: string
}

/** What one sub-query did, for the trace shown in the builder and to the model. */
export interface StepTrace {
  label: string
  table: string
  rows: number
  truncated?: boolean
  error?: string
  // Rows left out because they aren't published
  unpublished?: number
}

const cms = {
  from(table: string) {
    return (supabase as any).from(table)
  },
  rpc(fn: string, args: Record<string, unknown>) {
    return (supabase as any).rpc(fn, args)
  },
}

// ── Translations ─────────────────────────────────────────────────────────────

function pickTranslation(
  translations: Record<string, any> | null | undefined,
  preferred?: string,
  sourceLocale?: string,
  defaultLocale?: string,
): { value: any; locale?: string } {
  if (!translations || typeof translations !== "object") return { value: null }
  if (preferred && translations[preferred]) {
    return { value: translations[preferred], locale: preferred }
  }
  if (sourceLocale && translations[sourceLocale]) {
    return { value: translations[sourceLocale], locale: sourceLocale }
  }
  if (defaultLocale && translations[defaultLocale]) {
    return { value: translations[defaultLocale], locale: defaultLocale }
  }
  const keys = Object.keys(translations)
  if (!keys.length) return { value: null }
  return { value: translations[keys[0]], locale: keys[0] }
}

function titleOf(ctx: Ctx, row: any, fallback?: string): string {
  const value = tr(ctx, row).value
  return value?.title || value?.name || fallback || "(untitled)"
}

function toPlainText(html: unknown): string {
  if (typeof html !== "string") return ""
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

// ── Scoring ──────────────────────────────────────────────────────────────────

const STOPWORDS = new Set([
  "a", "an", "the", "and", "or", "but", "if", "of", "at", "by", "for", "with",
  "about", "to", "from", "in", "on", "is", "are", "was", "were", "be", "been",
  "do", "does", "did", "can", "could", "would", "should", "will", "i", "you",
  "my", "me", "we", "us", "it", "its", "this", "that", "there", "where", "what",
  "when", "who", "how", "why", "any", "some", "get", "need", "want", "please",
])

function queryTerms(query: string): string[] {
  const words = query.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  const meaningful = words.filter(w => w.length > 1 && !STOPWORDS.has(w))
  // A query of nothing but stopwords ("who are you") still has to search
  return meaningful.length ? meaningful : words
}

/** True when `term` appears in `hay` as a whole word. */
function wholeWordHit(hay: string, term: string): boolean {
  let from = 0
  for (;;) {
    const i = hay.indexOf(term, from)
    if (i < 0) return false
    const before = i === 0 ? "" : hay[i - 1]
    const after = hay[i + term.length] ?? ""
    const isWord = (c: string) => c !== "" && /[\p{L}\p{N}_]/u.test(c)
    if (!isWord(before) && !isWord(after)) return true
    from = i + 1
  }
}

function searchableText(value: unknown, out: string[] = [], depth = 0): string[] {
  if (depth > 6 || value == null) return out
  if (typeof value === "string") out.push(value)
  else if (typeof value === "number" || typeof value === "boolean") out.push(String(value))
  else if (Array.isArray(value)) for (const v of value) searchableText(v, out, depth + 1)
  else if (typeof value === "object") {
    for (const v of Object.values(value as Record<string, unknown>)) {
      searchableText(v, out, depth + 1)
    }
  }
  return out
}

function scoreRecord(record: unknown, terms: string[], title?: string): number {
  const hay = searchableText(record).join(" \n ").toLowerCase()
  if (!hay) return 0
  const titleHay = (title ?? "").toLowerCase()

  let score = 0
  let matched = 0
  for (const term of terms) {
    const inTitle = titleHay.includes(term)
    const inBody = hay.includes(term)
    if (!inTitle && !inBody) continue
    matched++
    if (inTitle) score += 10
    if (inBody) score += 1
    if (wholeWordHit(hay, term)) score += 2
  }
  if (!matched) return 0
  return score * (matched / terms.length)
}

// ── Area access ──────────────────────────────────────────────────────────────

function enabledAreas(params: CMSSearchWorker["parameters"]): Area[] {
  const on: Area[] = []
  if (params.searchArticles !== false) on.push("articles")
  if (params.searchServices !== false) on.push("services")
  if (params.searchSites !== false) on.push("site")
  return on
}

interface Ctx {
  team: string
  locale?: string
  defaultLocale?: string
  limit: number
  trace: StepTrace[]
  // For meaning search; without a key search is keyword only
  openaiKey?: string
  searchMode?: "hybrid" | "keyword"
  // Set when meaning search ran in this call, for the node's call list
  meaning?: MeaningRun
  // Tells the node a meaning search is in flight
  onMeaning?: (active: boolean) => void
  // The last search's results in rank order
  ranked?: RankedResult[]
}

export interface MeaningRun {
  // False when the search ran by words only
  used: boolean
  // Records meaning search found
  matches: number
  // Why it was words only
  skipped?: string
}

// One search result in final rank order, for the node's call list
export interface RankedResult {
  title: string
  kind: string
  // Matched the query's words
  words: boolean
  // Cosine similarity of the record's nearest section to the query, 0 to 1, when meaning search found it
  similarity?: number
  // Within Max Results, so the agent received it
  sent: boolean
  // Exactly what the agent received for it, when sent
  text?: string
}

// Ranked results kept for the call list; the rest are counted only
const RANKED_KEEP = 20

const tr = (ctx: Ctx, row: any) =>
  pickTranslation(row?.translations, ctx.locale, row?.source_locale, ctx.defaultLocale)

// One table per area that holds its documents
const CONTENT_TABLE: Record<Area, string> = {
  articles: "scms_kb_article",
  services: "scms_svc_service",
  site: "scms_site_page_content",
}

const PRESENCE_TTL_MS = 60_000

// Whether each area has any content. An area that can't be checked is left out, never treated as empty
type ContentPresence = Partial<Record<Area, boolean>>

// Per team and area, shared across runs: a live channel builds a new worker for every message.
// Short-lived, so content added in the CMS shows up within a minute
const presenceCache = new Map<string, { at: number; has: boolean }>()

export function clearContentPresenceCache() {
  presenceCache.clear()
}

async function contentPresence(team: string, areas: Area[]): Promise<ContentPresence> {
  const out: ContentPresence = {}
  await Promise.all(areas.map(async a => {
    const key = `${team}:${a}`
    const hit = presenceCache.get(key)
    if (hit && Date.now() - hit.at < PRESENCE_TTL_MS) { out[a] = hit.has; return }
    const { data, error } = await cms.from(CONTENT_TABLE[a]).select("id").eq("team_id", team).limit(1)
    if (error || !Array.isArray(data)) return
    out[a] = data.length > 0
    presenceCache.set(key, { at: Date.now(), has: out[a] })
  }))
  return out
}

const isEmptyCms = (has: ContentPresence, areas: Area[]) => areas.every(a => has[a] === false)

const emptyCmsMessage = (areas: Area[]) =>
  `The Smart CMS has no content for this team yet (${areas.map(a => AREA_LABEL[a]).join(", ")} are all empty). `
  + "Add content in the Smart CMS, or check the agent belongs to the right team."

const NO_TEAM_MESSAGE =
  "CMS Navigator has no team to search: the agent hasn't been saved, and the run didn't pass a team. Save the agent and run again."

const NO_AREAS_MESSAGE =
  "CMS Navigator has every area switched off. Turn on Articles, Service Map or Site Config."

// On a live channel the model is talking to a beneficiary, so it answers without the CMS instead of explaining setup
const LIVE_UNAVAILABLE =
  "No knowledge base content is available. Don't search further: answer without it, and don't mention the knowledge base or its setup."

async function loadDefaultLocale(ctx: Ctx): Promise<string | undefined> {
  const sites = await step<any>(ctx, "Default locale", "scms_site", () =>
    cms.from("scms_site").select("default_locale").eq("team_id", ctx.team).limit(1))
  if (sites[0]?.default_locale) return sites[0].default_locale
  const kbs = await step<any>(ctx, "KB default locale", "scms_kb", () =>
    cms.from("scms_kb").select("default_locale").eq("team_id", ctx.team).limit(1))
  return kbs[0]?.default_locale ?? undefined
}

// ── Published content only ────────────────────────────────────────────────

/**
 * The Navigator sees published content only, in every case: the editor, live
 * channels, and every action. A record whose every language is unpublished is
 * dropped; otherwise it keeps only its published translations, so a Spanish
 * draft of a live English article never reaches the agent. Each language's
 * own state wins, falling back to the record's, as in the Smart CMS.
 */
export function keepPublished(stateKey: "state" | "status") {
  return (row: any) => {
    if (!row) return null
    const recordState = row[stateKey] ?? "draft"
    const translations = row.translations && typeof row.translations === "object" ? row.translations : {}
    const locales = Object.keys(translations)
    // No translations: the record's own state decides
    if (!locales.length) return recordState === "published" ? row : null
    const published = Object.fromEntries(
      locales.filter(l => (translations[l]?.[stateKey] ?? recordState) === "published").map(l => [l, translations[l]]),
    )
    if (!Object.keys(published).length) return null
    return { ...row, translations: published }
  }
}

const PUBLISHED_ONLY: Record<string, ((row: any) => any) | undefined> = {
  scms_kb_article: keepPublished("state"),
  scms_svc_service: keepPublished("status"),
}

/** Runs one query, recording it in the trace whether or not it succeeds. */
async function step<T>(
  ctx: Ctx, label: string, table: string, run: () => Promise<{ data: T[] | null; error: any }>,
  limit: number = FETCH_CEILING,
): Promise<T[]> {
  const entry: StepTrace = { label, table, rows: 0 }
  ctx.trace.push(entry)
  try {
    const { data, error } = await run()
    if (error) throw error
    const raw = data ?? []
    if (raw.length >= limit) entry.truncated = true
    const keep = PUBLISHED_ONLY[table]
    const rows = keep ? (raw.map(keep).filter(Boolean) as T[]) : raw
    entry.rows = rows.length
    if (raw.length > rows.length) entry.unpublished = raw.length - rows.length
    return rows
  } catch (err) {
    entry.error = errText(err)
    return []
  }
}

function errText(err: unknown): string {
  if (!err) return "unknown error"
  if (err instanceof Error) return err.message
  if (typeof err === "object") {
    const e = err as Record<string, unknown>
    const parts = [e.message, e.details, e.hint].filter(Boolean).map(String)
    if (parts.length) return parts.join(" — ")
    const code = e.code ? `code ${e.code}` : ""
    return code || JSON.stringify(err)
  }
  return String(err)
}

// ── Listing: articles ────────────────────────────────────────────────────────

async function listArticleArea(ctx: Ctx, segments: CmsPath["segments"]): Promise<Entry[]> {
  const last = segments[segments.length - 1]

  // articles/ -> the categories
  if (!last) {
    const [cats, secs] = await Promise.all([
      step(ctx, "Article categories", "scms_kb_category", () =>
        cms.from("scms_kb_category").select("id, translations, position, source_locale").eq("team_id", ctx.team)),
      step<any>(ctx, "Article sections", "scms_kb_section", () =>
        cms.from("scms_kb_section").select("id, category_id").eq("team_id", ctx.team)),
    ])
    const perCategory = new Map<string, number>()
    for (const s of secs) {
      perCategory.set(s.category_id, (perCategory.get(s.category_id) ?? 0) + 1)
    }
    return sortByPosition(cats).map((c: any) => ({
      path: pathOf("articles", { kind: "category", id: c.id }),
      kind: "category",
      title: titleOf(ctx, c),
      childCount: perCategory.get(c.id) ?? 0,
    }))
  }

  // articles/category:X -> its top-level sections
  if (last.kind === "category") {
    const secs = await step<any>(ctx, "Sections in category", "scms_kb_section", () =>
      cms.from("scms_kb_section")
        .select("id, translations, position, parent_section_id, source_locale")
        .eq("team_id", ctx.team).eq("category_id", last.id))
    // parent_section_id null means "top level of the category".
    const top = secs.filter(s => !s.parent_section_id)
    return withSectionCounts(ctx, top)
  }

  // articles/.../section:X -> nested sections, then articles
  if (last.kind === "section") {
    const [subs, arts] = await Promise.all([
      step<any>(ctx, "Sub-sections", "scms_kb_section", () =>
        cms.from("scms_kb_section")
          .select("id, translations, position, parent_section_id, source_locale")
          .eq("team_id", ctx.team).eq("parent_section_id", last.id)),
      step<any>(ctx, "Articles in section", "scms_kb_article", () =>
        cms.from("scms_kb_article")
          .select("id, translations, position, state, source_locale")
          .eq("team_id", ctx.team).eq("section_id", last.id).limit(FETCH_CEILING)),
    ])
    const subEntries = await withSectionCounts(ctx, subs)
    const artEntries: Entry[] = sortByPosition(arts).map((a: any) => ({
      path: pathOf("articles", { kind: "article", id: a.id }),
      url: articleUrl(a),
      kind: "article",
      title: titleOf(ctx, a),
      readable: true,
      detail: articleDetail(a),
    }))
    return [...subEntries, ...artEntries]
  }

  if (last.kind === "article") {
    return [] // A leaf: use action "read".
  }
  return []
}

async function withSectionCounts(ctx: Ctx, sections: any[]): Promise<Entry[]> {
  if (!sections.length) return []
  const ids = sections.map(s => s.id)
  const [arts, subs] = await Promise.all([
    step<any>(ctx, "Article counts", "scms_kb_article", () =>
      cms.from("scms_kb_article").select("id, section_id, state, translations")
        .eq("team_id", ctx.team).in("section_id", ids).limit(FETCH_CEILING)),
    step<any>(ctx, "Sub-section counts", "scms_kb_section", () =>
      cms.from("scms_kb_section").select("id, parent_section_id")
        .eq("team_id", ctx.team).in("parent_section_id", ids)),
  ])
  const count = new Map<string, number>()
  for (const a of arts) count.set(a.section_id, (count.get(a.section_id) ?? 0) + 1)
  for (const s of subs) count.set(s.parent_section_id, (count.get(s.parent_section_id) ?? 0) + 1)

  return sortByPosition(sections).map((s: any) => ({
    path: pathOf("articles", { kind: "section", id: s.id }),
    kind: "section",
    title: titleOf(ctx, s),
    childCount: count.get(s.id) ?? 0,
  }))
}

/** Locale coverage and publish state — what a content editor needs to see. */
// Rows arrive with published translations only; see keepPublished
function articleDetail(a: any): string {
  const locales = Object.keys(a.translations ?? {})
  return `published in ${locales.join(", ") || "no language"}`
}

const sortByPosition = <T extends { position?: number }>(rows: T[]) =>
  [...rows].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

// ── Listing: services ────────────────────────────────────────────────────────

const SERVICE_COLS = "id, name, description, translations, status, address, "
  + "country_id, region_id, city_id, provider_id, category_ids, oldid"

async function listServiceArea(ctx: Ctx, segments: CmsPath["segments"]): Promise<Entry[]> {
  const last = segments[segments.length - 1]

  if (!last) {
    const [countries, cats, providers] = await Promise.all([
      step<any>(ctx, "Countries", "scms_svc_country", () =>
        cms.from("scms_svc_country").select("id, name, translations").eq("team_id", ctx.team)),
      step<any>(ctx, "Service categories", "scms_svc_service_category", () =>
        cms.from("scms_svc_service_category").select("id, name, translations, status").eq("team_id", ctx.team)),
      step<any>(ctx, "Providers", "scms_svc_provider", () =>
        cms.from("scms_svc_provider").select("id, name, translations, status").eq("team_id", ctx.team)),
    ])
    return [
      ...countries.map((c: any) => ({
        path: pathOf("services", { kind: "country", id: c.id }),
        kind: "country",
        title: titleOf(ctx, c, c.name),
        detail: "by geography",
      })),
      ...cats.map((c: any) => ({
        path: pathOf("services", { kind: "category", id: c.id }),
        kind: "category",
        title: titleOf(ctx, c, c.name),
        detail: c.status && c.status !== "published" ? `by category · ${c.status}` : "by category",
      })),
      // Providers are the organisations delivering services -- a first-class
      // reachable at all.
      ...providers.map((p: any) => ({
        path: pathOf("services", { kind: "provider", id: p.id }),
        kind: "provider",
        title: titleOf(ctx, p, p.name),
        detail: p.status && p.status !== "Active" ? `by provider · ${p.status}` : "by provider",
      })),
    ]
  }

  if (last.kind === "country") {
    const [regions, direct] = await Promise.all([
      step<any>(ctx, "Regions", "scms_svc_region", () =>
        cms.from("scms_svc_region").select("id, name, translations")
          .eq("team_id", ctx.team).eq("country_id", last.id)),
      step<any>(ctx, "Services in country", "scms_svc_service", () =>
        cms.from("scms_svc_service").select(SERVICE_COLS)
          .eq("team_id", ctx.team).eq("country_id", last.id).limit(FETCH_CEILING)),
    ])
    // Services with no region would otherwise be unreachable by browsing.
    const orphans = direct.filter(s => !s.region_id)
    return [
      ...regions.map((r: any) => ({
        path: pathOf("services", { kind: "region", id: r.id }),
        kind: "region",
        title: titleOf(ctx, r, r.name),
      })),
      ...orphans.map(serviceEntry(ctx)),
    ]
  }

  if (last.kind === "region") {
    const [cities, direct] = await Promise.all([
      step<any>(ctx, "Cities", "scms_svc_city", () =>
        cms.from("scms_svc_city").select("id, name, translations")
          .eq("team_id", ctx.team).eq("region_id", last.id)),
      step<any>(ctx, "Services in region", "scms_svc_service", () =>
        cms.from("scms_svc_service").select(SERVICE_COLS)
          .eq("team_id", ctx.team).eq("region_id", last.id).limit(FETCH_CEILING)),
    ])
    const orphans = direct.filter(s => !s.city_id)
    return [
      ...cities.map((c: any) => ({
        path: pathOf("services", { kind: "city", id: c.id }),
        kind: "city",
        title: titleOf(ctx, c, c.name),
      })),
      ...orphans.map(serviceEntry(ctx)),
    ]
  }

  if (last.kind === "city") {
    const svcs = await step<any>(ctx, "Services in city", "scms_svc_service", () =>
      cms.from("scms_svc_service").select(SERVICE_COLS)
        .eq("team_id", ctx.team).eq("city_id", last.id).limit(FETCH_CEILING))
    return svcs.map(serviceEntry(ctx))
  }

  if (last.kind === "provider") {
    const svcs = await step<any>(ctx, "Services from provider", "scms_svc_service", () =>
      cms.from("scms_svc_service").select(SERVICE_COLS)
        .eq("team_id", ctx.team).eq("provider_id", last.id).limit(FETCH_CEILING))
    return svcs.map(serviceEntry(ctx))
  }

  if (last.kind === "category") {
    const svcs = await step<any>(ctx, "Services in category", "scms_svc_service", () =>
      cms.from("scms_svc_service").select(SERVICE_COLS)
        .eq("team_id", ctx.team).limit(FETCH_CEILING))
    return svcs.filter(s => asIdList(s.category_ids).includes(last.id)).map(serviceEntry(ctx))
  }

  return []
}

async function resolveGeography(ctx: Ctx, s: any): Promise<string[]> {
  const lookups: Array<Promise<string | null>> = [
    lookupName(ctx, "scms_svc_country", s.country_id),
    lookupName(ctx, "scms_svc_region", s.region_id),
    lookupName(ctx, "scms_svc_city", s.city_id),
  ]
  return (await Promise.all(lookups)).filter((n): n is string => !!n)
}

async function lookupName(ctx: Ctx, table: string, id: string | null): Promise<string | null> {
  if (!id) return null
  const rows = await step<any>(ctx, `Resolve ${table.replace("scms_svc_", "")}`, table, () =>
    cms.from(table).select("id, name, translations").eq("team_id", ctx.team).eq("id", id))
  const row = rows[0]
  if (!row) return null
  return tr(ctx, row).value?.name || row.name || null
}

/** An id-array column, tolerating either a Postgres array or a jsonb array. */
function asIdList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String)
  if (typeof v === "string") {
    // A Postgres text[] can arrive as the literal "{a,b}" over PostgREST.
    const trimmed = v.trim()
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      return trimmed.slice(1, -1).split(",").map(s => s.replace(/^"|"$/g, "").trim()).filter(Boolean)
    }
    try {
      const parsed = JSON.parse(trimmed)
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }
  return []
}

const serviceEntry = (ctx: Ctx) => (s: any): Entry => {
  const t = tr(ctx, s).value
  return {
    path: pathOf("services", { kind: "service", id: s.id }),
    url: serviceUrl(s),
    kind: "service",
    title: titleOf(ctx, s, s.name),
    readable: true,
    // Status is PER LOCALE (types.ts serviceStatusFor): a service can be
    // published in English and draft in Arabic, so the record-level status
    // alone misreports what a given locale actually sees.
    detail: [t?.status ?? s.status, t?.address ?? s.address].filter(Boolean).join(" · ") || undefined,
  }
}

// ── Listing: site ────────────────────────────────────────────────────────────

async function listSiteArea(ctx: Ctx, segments: CmsPath["segments"]): Promise<Entry[]> {
  const last = segments[segments.length - 1]

  if (!last) {
    const sites = await step<any>(ctx, "Sites", "scms_site", () =>
      cms.from("scms_site").select("id, name, base_url, locales, content_ids").eq("team_id", ctx.team))
    return sites.map((s: any) => ({
      path: pathOf("site", { kind: "site", id: s.id }),
      kind: "site",
      title: s.name || "(unnamed site)",
      childCount: asIdList(s.content_ids).length,
      detail: [s.base_url, asIdList(s.locales).join(", ")].filter(Boolean).join(" · ") || undefined,
    }))
  }

  if (last.kind === "site") {
    const sites = await step<any>(ctx, "Site content ids", "scms_site", () =>
      cms.from("scms_site").select("content_ids").eq("team_id", ctx.team).eq("id", last.id))
    const ids = asIdList(sites[0]?.content_ids)
    if (!ids.length) return []
    const blocks = await step<any>(ctx, "Page content", "scms_site_page_content", () =>
      cms.from("scms_site_page_content").select("id, type, title, translations")
        .eq("team_id", ctx.team).in("id", ids).limit(FETCH_CEILING))
    // content_ids is render order, and the database does not preserve it.
    const byId = new Map(blocks.map((b: any) => [b.id, b]))
    return ids.filter(id => byId.has(id)).map(id => {
      const b: any = byId.get(id)
      return {
        path: pathOf("site", { kind: "block", id: b.id }),
        kind: "block",
        title: titleOf(ctx, b, b.title) || b.type,
        readable: true,
        detail: b.type,
      }
    })
  }

  return []
}

// ── Reading ──────────────────────────────────────────────────────────────────

async function readNode(ctx: Ctx, area: Area, seg: { kind: string; id: string }): Promise<string> {
  if (area === "articles" && seg.kind === "article") {
    const rows = await step<any>(ctx, "Read article", "scms_kb_article", () =>
      cms.from("scms_kb_article").select("id, translations, section_id, state, labels, attachments, source_locale")
        .eq("team_id", ctx.team).eq("id", seg.id))
    const a = rows[0]
    if (!a) return `No article with id ${seg.id}.`
    const { value, locale } = tr(ctx, a)
    if (!value) return `Article ${seg.id} has no translations.`
    const parts = [
      `# ${value.title || "(untitled)"}`,
      `url: ${articleUrl(a)}`,
      `_${[`locale: ${locale}`, `state: ${value.state ?? a.state}`,
        a.labels?.length ? `labels: ${a.labels.join(", ")}` : null,
      ].filter(Boolean).join(" · ")}_`,
      "",
      toPlainText(value.body) || value.description || "(no body)",
    ]
    if (a.attachments?.length) {
      parts.push("", "Attachments:", ...a.attachments.map((f: any) => `- ${f.fileName}: ${f.url}`))
    }
    const others = Object.keys(a.translations ?? {}).filter(l => l !== locale)
    if (others.length) parts.push("", `Also available in: ${others.join(", ")}`)
    return parts.join("\n")
  }

  if (area === "services" && seg.kind === "service") {
    // Column names are snake_case; see SERVICE_COLS.
    const rows = await step<any>(ctx, "Read service", "scms_svc_service", () =>
      cms.from("scms_svc_service")
        .select("id, name, description, translations, status, address, contact_info, hours, "
          + "always_open, provider_id, country_id, region_id, city_id, category_ids, oldid")
        .eq("team_id", ctx.team).eq("id", seg.id))
    const s = rows[0]
    if (!s) return `No service with id ${seg.id}.`

    const catIds = asIdList(s.category_ids)
    const [providers, geo, cats] = await Promise.all([
      s.provider_id
        ? step<any>(ctx, "Service provider", "scms_svc_provider", () =>
          cms.from("scms_svc_provider").select("id, name, translations")
            .eq("team_id", ctx.team).eq("id", s.provider_id))
        : Promise.resolve([]),
      resolveGeography(ctx, s),
      catIds.length
        ? step<any>(ctx, "Service categories", "scms_svc_service_category", () =>
          cms.from("scms_svc_service_category").select("id, name, translations")
            .eq("team_id", ctx.team).in("id", catIds))
        : Promise.resolve([]),
    ])

    const { value, locale } = tr(ctx, s)
    // Per-locale status, falling back to the record's own.
    const status = value?.status ?? s.status
    const parts = [
      `# ${value?.name || s.name || "(unnamed service)"}`,
      `url: ${serviceUrl(s)}`,
      `_${[`status: ${status}`, locale && `locale: ${locale}`].filter(Boolean).join(" · ")}_`,
      "",
      toPlainText(value?.description ?? s.description) || "(no description)",
    ]

    const provider = providers[0]
    if (provider) {
      const pName = tr(ctx, provider).value?.name || provider.name
      parts.push("", `Provider: ${pName}  (${pathOf("services", { kind: "provider", id: provider.id })})`)
    }
    if (geo.length) parts.push(`Location: ${geo.join(" / ")}`)
    if (cats.length) {
      parts.push(`Categories: ${cats.map((c: any) =>
        tr(ctx, c).value?.name || c.name).join(", ")}`)
    }

    const address = value?.address ?? s.address
    if (address) parts.push(`Address: ${address}`)
    if (s.always_open) parts.push("Hours: always open")
    else if (s.hours?.length) {
      parts.push("", "Hours:", ...s.hours.map((h: any) => `- ${h.day}: ${h.open}–${h.close}`))
    }
    if (s.contact_info?.length) {
      parts.push("", "Contact:",
        ...s.contact_info.map((c: any) => `- ${c.channel}: ${c.contactDetails}`))
    }

    const others = Object.keys(s.translations ?? {}).filter(l => l !== locale)
    if (others.length) parts.push("", `Also available in: ${others.join(", ")}`)
    return parts.join("\n")
  }

  if (area === "services" && seg.kind === "provider") {
    const rows = await step<any>(ctx, "Read provider", "scms_svc_provider", () =>
      cms.from("scms_svc_provider")
        .select("id, name, description, translations, status, address, country_id")
        .eq("team_id", ctx.team).eq("id", seg.id))
    const p = rows[0]
    if (!p) return `No provider with id ${seg.id}.`
    const { value, locale } = tr(ctx, p)
    const parts = [
      `# ${value?.name || p.name || "(unnamed provider)"}`,
      `_${[`status: ${p.status}`, locale && `locale: ${locale}`].filter(Boolean).join(" · ")}_`,
      "",
      toPlainText(value?.description ?? p.description) || "(no description)",
    ]
    const address = value?.address ?? p.address
    if (address) parts.push("", `Address: ${address}`)
    parts.push("", `List ${pathOf("services", { kind: "provider", id: p.id })} to see this provider's services.`)
    return parts.join("\n")
  }

  if (area === "site" && seg.kind === "block") {
    const rows = await step<any>(ctx, "Read page block", "scms_site_page_content", () =>
      cms.from("scms_site_page_content").select("id, type, title, fields, translations")
        .eq("team_id", ctx.team).eq("id", seg.id))
    const b = rows[0]
    if (!b) return `No page-content block with id ${seg.id}.`
    const { value, locale } = tr(ctx, b)
    const f = b.fields ?? {}
    // The block model is flat, so prose lives under one of several field
    // names depending on the block type.
    const body = toPlainText(
      value?.text ?? value?.richtext ?? f.text ?? f.richtext ?? f.aboutText ?? "",
    )
    return [
      `# ${value?.title || b.title || b.type}`,
      `_${[`type: ${b.type}`, locale && `locale: ${locale}`].filter(Boolean).join(" · ")}_`,
      "",
      body || "(no text content)",
    ].join("\n")
  }

  return `"${seg.kind}" is a branch, not a document. Use action "list" on this path to see `
    + `what it contains; only ${readableKinds(area)} can be read.`
}

const readableKinds = (area: Area) =>
  area === "articles" ? "articles"
    : area === "services" ? "services and providers"
      : "page-content blocks"

// ── Search ───────────────────────────────────────────────────────────────────

interface Hit extends Entry {
  score: number
  body: string
  // Nearest section's similarity to the query, when meaning search found it
  similarity?: number
  matched?: "words" | "meaning" | "both"
}

async function searchArea(
  ctx: Ctx, area: Area, query: string, scope: CmsPath["segments"], meaning: Meaning | null = null,
  words: Set<string> | null = null,
): Promise<Hit[]> {
  const terms = queryTerms(query)
  const hits: Hit[] = []
  // A record is a candidate when its words match or its meaning does
  const near = (kind: string, id: string) => meaning?.get(`${area}:${kind}:${id}`)
  // With database word matching, only the records either search found are loaded; without it, everything is
  const only = (kind: string): string[] | null => {
    if (!words) return null
    const ids = new Set<string>()
    for (const key of [...words, ...(meaning?.keys() ?? [])]) {
      const [a, k, id] = key.split(":")
      if (a === area && k === kind) ids.add(id)
    }
    return [...ids]
  }
  const label = (what: string) => (words ? `${what} loaded` : `${what} checked for words`)

  if (area === "articles") {
    const sectionIds = await articleScopeSections(ctx, scope)
    // null means unscoped; an empty array means the scope holds nothing.
    if (sectionIds && !sectionIds.length) return []
    const ids = only("article")
    const rows = ids && !ids.length ? [] : await step<any>(ctx, label("Articles"), "scms_kb_article", () => {
      let q = cms.from("scms_kb_article").select("id, translations, section_id, state, source_locale").eq("team_id", ctx.team)
      if (sectionIds) q = q.in("section_id", sectionIds)
      if (ids) q = q.in("id", ids)
      return q.limit(FETCH_CEILING)
    })
    for (const a of rows) {
      const { value } = tr(ctx, a)
      if (!value) continue
      const score = scoreRecord(a.translations, terms, value.title)
      const similarity = near("article", a.id)
      if (!score && similarity === undefined) continue
      hits.push({
        similarity,
        path: pathOf("articles", { kind: "article", id: a.id }),
        url: articleUrl(a),
        kind: "article", title: value.title || "(untitled)", readable: true,
        detail: articleDetail(a), score,
        body: toPlainText(value.body ?? value.description),
      })
    }
  }

  if (area === "services") {
    const serviceIds = only("service")
    const rows = serviceIds && !serviceIds.length ? [] : await step<any>(ctx, label("Services"), "scms_svc_service", () => {
        let q = cms.from("scms_svc_service").select(SERVICE_COLS).eq("team_id", ctx.team)
        if (serviceIds) q = q.in("id", serviceIds)
        // Geography and provider scopes narrow in SQL; category is an id
        // array, filtered below for the reason given in listServiceArea.
        for (const s of scope) {
          if (s.kind === "country") q = q.eq("country_id", s.id)
          if (s.kind === "region") q = q.eq("region_id", s.id)
          if (s.kind === "city") q = q.eq("city_id", s.id)
          if (s.kind === "provider") q = q.eq("provider_id", s.id)
        }
        return q.limit(FETCH_CEILING)
      })
    // Matched providers, plus the providers of loaded services, whose names services are found by
    const providerIds = only("provider")
    const wanted = providerIds && [...new Set([...providerIds, ...rows.map((s: any) => s.provider_id).filter(Boolean)])]
    const loadedProviders = wanted && !wanted.length ? [] : await step<any>(ctx, label("Providers"), "scms_svc_provider", () => {
      let q = cms.from("scms_svc_provider")
        .select("id, name, description, address, status, translations")
        .eq("team_id", ctx.team)
      if (wanted) q = q.in("id", wanted)
      return q.limit(FETCH_CEILING)
    })
    // Only matched providers are results; the rest just lend their names
    const providers = providerIds ? loadedProviders.filter((p: any) => providerIds.includes(p.id)) : loadedProviders

    const categoryScope = scope.filter(s => s.kind === "category").map(s => s.id)
    const scoped = categoryScope.length
      ? rows.filter(s => categoryScope.every(id => asIdList(s.category_ids).includes(id)))
      : rows

    // Provider name -> id, so a service can inherit its provider's score.
    const providerById = new Map<string, any>(loadedProviders.map((p: any) => [p.id, p]))
    for (const p of providers) {
      const { value } = tr(ctx, p)
      const score = scoreRecord(
        { name: p.name, description: p.description, address: p.address, translations: p.translations },
        terms, value?.name || p.name,
      )
      const similarity = near("provider", p.id)
      if (!score && similarity === undefined) continue
      hits.push({
        similarity,
        path: pathOf("services", { kind: "provider", id: p.id }),
        // Readable: readNode has a provider branch, and readableKinds()
        // advertises "services and providers". Without this the entry renders
        // [dir], contradicting both.
        kind: "provider", title: value?.name || p.name || "(unnamed provider)",
        readable: true,
        detail: "provider — read it, or list it to see its services", score,
        body: toPlainText(value?.description ?? p.description),
      })
    }

    for (const s of scoped) {
      const { value } = tr(ctx, s)
      const provider = s.provider_id ? providerById.get(s.provider_id) : undefined
      const providerName = provider
        ? (tr(ctx, provider).value?.name || provider.name)
        : undefined
      // The provider's name is part of what a service is findable by.
      const searchable = {
        name: s.name, description: s.description, address: s.address,
        translations: s.translations, provider: providerName,
      }
      const score = scoreRecord(searchable, terms, value?.name || s.name)
      const similarity = near("service", s.id)
      if (!score && similarity === undefined) continue
      const description = toPlainText(value?.description ?? s.description)
      const address = value?.address ?? s.address
      const status = value?.status ?? s.status
      hits.push({
        similarity,
        path: pathOf("services", { kind: "service", id: s.id }),
        url: serviceUrl(s),
        kind: "service", title: value?.name || s.name || "(unnamed service)", readable: true,
        detail: [status, address].filter(Boolean).join(" · ") || undefined, score,
        body: [
          description,
          address && `Address: ${address}`,
          providerName && `Provider: ${providerName}`,
        ].filter(Boolean).join("\n"),
      })
    }
  }

  if (area === "site") {
    const ids = only("block")
    const rows = ids && !ids.length ? [] : await step<any>(ctx, label("Site blocks"), "scms_site_page_content", () => {
      let q = cms.from("scms_site_page_content").select("id, type, title, fields, translations").eq("team_id", ctx.team)
      if (ids) q = q.in("id", ids)
      return q.limit(FETCH_CEILING)
    })
    for (const b of rows) {
      const { value } = tr(ctx, b)
      // `type` is a block name and `id` a ULID; neither is content.
      const score = scoreRecord(
        { title: b.title, fields: b.fields, translations: b.translations },
        terms, value?.title || b.title,
      )
      const similarity = near("block", b.id)
      if (!score && similarity === undefined) continue
      const f = b.fields ?? {}
      hits.push({
        similarity,
        path: pathOf("site", { kind: "block", id: b.id }),
        kind: "block", title: value?.title || b.title || b.type, readable: true,
        detail: b.type, score,
        body: toPlainText(value?.text ?? value?.richtext ?? f.text ?? f.richtext ?? f.aboutText ?? ""),
      })
    }
  }

  return hits
}

// ── Hybrid search ────────────────────────────────────────────────────────────

/**
 * HYBRID SEARCH
 *
 * When a team turns on "Search by meaning" in the Smart CMS, every record's
 * text is stored as vectors, per language and section (scms_embedding). A
 * search then runs two ways at once:
 *
 *   words    the keyword scoring above, over the rows searchArea fetches
 *   meaning  the query's vector against the stored ones (scms_match), which
 *            finds "somewhere to sleep" in an article about shelters, and
 *            matches across languages
 *
 * The two rankings are merged with Reciprocal Rank Fusion: each record scores
 * 1/(60 + rank) in each list it appears in, summed. Rank, not raw score, so
 * the two scales never need comparing, and a record both find ranks highest.
 *
 * Words only, skipping meaning: quoted text, phone numbers, emails, ids and
 * links, where the exact string is the point; a team with no vectors; no
 * OpenAI key; or the node set to keyword search.
 */

// area:kind:id -> similarity of the record's nearest section
type Meaning = Map<string, number>

const RRF_K = 60
const MEANING_CANDIDATES = 50
const INDEX_TTL_MS = 60_000

// Per team, shared across runs like contentPresence
const indexCache = new Map<string, { at: number; areas: Area[] }>()

export function clearMeaningIndexCache() {
  indexCache.clear()
}

// Queries where the exact string is the point: quotes, phone numbers, emails, ids, long numbers, links
export function isExactQuery(query: string): boolean {
  const q = query.trim()
  return /["\u201c\u201d]/.test(q)
    || /\+?\d[\d\s().-]{6,}\d/.test(q)
    || /\S+@\S+\.\S+/.test(q)
    || /\b[0-9A-HJKMNP-TV-Z]{26}\b/.test(q)
    || /\b\d{5,}\b/.test(q)
    || /https?:\/\//i.test(q)
}

// Areas that have vectors. No migration, or an error, reads as none
async function indexedAreas(team: string): Promise<Area[]> {
  const hit = indexCache.get(team)
  if (hit && Date.now() - hit.at < INDEX_TTL_MS) return hit.areas
  const { data, error } = await (async () => cms.rpc("scms_indexed_areas", { p_team: team }))()
    .catch(e => ({ data: null, error: e }))
  const areas = error || !Array.isArray(data)
    ? []
    : (data as string[]).filter((a): a is Area => (AREAS as readonly string[]).includes(a))
  indexCache.set(team, { at: Date.now(), areas })
  return areas
}

async function meaningMatches(ctx: Ctx, areas: Area[], query: string): Promise<Meaning | null> {
  const skip = (skipped: string) => { ctx.meaning = { used: false, matches: 0, skipped }; return null }
  if (!query.trim()) return null
  if (ctx.searchMode === "keyword") return skip("the node is set to search by words only")
  if (!ctx.openaiKey) return skip("the team has no OpenAI key")
  if (isExactQuery(query)) return skip("the query looks for exact text (quotes, a number, an email or an id)")
  const indexed = await indexedAreas(ctx.team)
  const target = areas.filter(a => indexed.includes(a))
  if (!target.length) return skip(`${areas.map(a => AREA_LABEL[a]).join(", ")} ${areas.length === 1 ? "isn't" : "aren't"} indexed yet`)
  ctx.onMeaning?.(true)
  let rows: any[]
  try {
    rows = await step<any>(ctx, "Found by meaning", "scms_match", async () => {
      const embedding = await createEmbedding(query, ctx.openaiKey!)
      return cms.rpc("scms_match", { p_team: ctx.team, p_embedding: embedding, p_areas: target, p_limit: MEANING_CANDIDATES })
    }, MEANING_CANDIDATES)
  } finally {
    ctx.onMeaning?.(false)
  }
  const failed = ctx.trace.find(t => t.table === "scms_match" && t.error)
  if (failed) return skip(`meaning search failed: ${failed.error}`)
  ctx.meaning = { used: true, matches: rows.length }
  if (!rows.length) return null
  return new Map(rows.map((r: any) => [`${r.area}:${r.kind}:${r.record_id}`, Number(r.similarity)]))
}

/**
 * WORD MATCHING IN THE DATABASE
 *
 * scms_keyword_match finds the records whose published text contains any of
 * the query's words, in every language, and returns their ids. Only those
 * records are then loaded and scored here, so a search never downloads the
 * whole CMS and has no row limit. Without the function (migration not run)
 * search falls back to loading every record and checking it here.
 */

// The best word matches loaded and ranked here; the database returns every match, best first
const WORD_CANDIDATES = 150

// area:kind:id of every record whose words match; null when the database can't do it
async function wordMatches(ctx: Ctx, areas: Area[], query: string): Promise<Set<string> | null> {
  const terms = queryTerms(query)
  if (!terms.length) return null
  const entry: StepTrace = { label: "Found by words", table: "scms_keyword_match", rows: 0 }
  let data: any
  try {
    const res = await cms.rpc("scms_keyword_match", {
      p_team: ctx.team, p_terms: terms, p_areas: areas, p_limit: WORD_CANDIDATES,
    })
    if (res.error) return null
    data = res.data
  } catch {
    return null
  }
  // Not set up yet: fall back quietly to checking every record
  if (!Array.isArray(data)) return null
  entry.rows = data.length
  ctx.trace.push(entry)
  // Already ordered by title matches, then words matched
  return new Set(data.slice(0, WORD_CANDIDATES).map((r: any) => `${r.area}:${r.kind}:${r.record_id}`))
}

// Merges the words and meaning rankings; score becomes the fused score
export function fuseRankings<T extends { score: number; similarity?: number; matched?: Hit["matched"] }>(hits: T[]): T[] {
  const byWords = hits.filter(h => h.score > 0).sort((a, b) => b.score - a.score)
  const byMeaning = hits.filter(h => h.similarity !== undefined).sort((a, b) => b.similarity! - a.similarity!)
  const fused = new Map<T, number>()
  byWords.forEach((h, i) => fused.set(h, (fused.get(h) ?? 0) + 1 / (RRF_K + i + 1)))
  byMeaning.forEach((h, i) => fused.set(h, (fused.get(h) ?? 0) + 1 / (RRF_K + i + 1)))
  return hits.map(h => ({
    ...h,
    score: fused.get(h) ?? 0,
    matched: h.score > 0 && h.similarity !== undefined ? "both" : h.score > 0 ? "words" : "meaning",
  }))
}

// Every search goes through here: words always, meaning too when it can
async function searchCms(ctx: Ctx, areas: Area[], query: string, scope: CmsPath["segments"]): Promise<Hit[]> {
  const [meaning, words] = await Promise.all([meaningMatches(ctx, areas, query), wordMatches(ctx, areas, query)])
  const results = await Promise.all(areas.map(a => searchArea(ctx, a, query, scope, meaning, words)))
  const hits = meaning ? fuseRankings(results.flat()) : results.flat()
  if (ctx.meaning?.used) ctx.meaning.matches = hits.filter(h => h.similarity !== undefined).length
  ctx.ranked = [...hits].sort((a, b) => b.score - a.score).slice(0, RANKED_KEEP).map((h, i) => ({
    title: h.title, kind: h.kind,
    words: !meaning || h.matched !== "meaning",
    similarity: h.similarity,
    sent: i < ctx.limit,
    text: i < ctx.limit ? formatHit(h) : undefined,
  }))
  return hits
}

async function articleScopeSections(ctx: Ctx, scope: CmsPath["segments"]): Promise<string[] | null> {
  const last = scope[scope.length - 1]
  if (!last) return null
  // Checked before the query: an article scope discards the result.
  if (last.kind === "article") return [] // A leaf scope: read it instead.

  // Explicitly capped. Left unbounded, PostgREST applies its own 1000-row
  // truncation FETCH_CEILING exists to make visible.
  const all = await step<any>(ctx, "Section tree", "scms_kb_section", () =>
    cms.from("scms_kb_section").select("id, category_id, parent_section_id")
      .eq("team_id", ctx.team).limit(FETCH_CEILING))

  const roots = last.kind === "category"
    ? all.filter(s => s.category_id === last.id && !s.parent_section_id).map(s => s.id)
    : [last.id]

  const out = new Set<string>(roots)
  let frontier = new Set<string>(roots)
  while (frontier.size) {
    // walk over every section in the KB.
    const next = all.filter(s => frontier.has(s.parent_section_id)).map(s => s.id)
    frontier = new Set(next.filter(id => !out.has(id)))
    for (const id of frontier) out.add(id)
  }
  return [...out]
}

// ── Tree ─────────────────────────────────────────────────────────────────────

/** Several levels at once, when listing one at a time would take too many calls. */
async function buildTree(
  ctx: Ctx, area: Area, segments: CmsPath["segments"], depth: number,
): Promise<string[]> {
  const lines: string[] = []
  const seen = new Set<string>()

  const walk = async (segs: CmsPath["segments"], level: number, indent: string) => {
    if (level > depth) return
    const entries = await listArea(ctx, area, segs)
    for (const e of entries) {
      const size = e.childCount != null ? ` (${e.childCount})` : ""
      lines.push(`${indent}${e.readable ? "-" : "+"} ${e.title}${size}  ${e.path}`)
      if (e.readable || level >= depth || seen.has(e.path)) continue
      seen.add(e.path)
      // re-derived by slicing — one definition of the path format, not two.
      const parsed = parsePath(e.path)
      if ("error" in parsed) continue
      await walk(parsed.segments, level + 1, `${indent}  `)
    }
  }
  await walk(segments, 1, "")
  return lines
}

function listArea(ctx: Ctx, area: Area, segments: CmsPath["segments"]): Promise<Entry[]> {
  if (area === "articles") return listArticleArea(ctx, segments)
  if (area === "services") return listServiceArea(ctx, segments)
  return listSiteArea(ctx, segments)
}

// ── Formatting ───────────────────────────────────────────────────────────────

function formatEntries(entries: Entry[], header: string): string {
  if (!entries.length) {
    return `${header}\n\n(empty — nothing is filed here)`
  }
  const lines = entries.map(e => {
    const bits = [e.title]
    if (e.childCount != null) bits.push(`(${e.childCount})`)
    if (e.detail) bits.push(`— ${e.detail}`)
    return `${e.readable ? "[doc]" : "[dir]"} ${bits.join(" ")}\n      path: ${e.path}${e.url ? `\n      url: ${e.url}` : ""}`
  })
  return `${header}\n\n${lines.join("\n")}`
}

function formatTrace(trace: StepTrace[]): string {
  if (!trace.length) return ""
  const lines = trace.map(s =>
    s.error
      ? `  x ${s.label} (${s.table}): FAILED — ${s.error}`
      : `  . ${s.label} (${s.table}): ${s.rows} row${s.rows === 1 ? "" : "s"}`
        + (s.unpublished ? ` (${s.unpublished} unpublished left out)` : "")
        + (s.truncated ? " (CAPPED — more exist)" : ""))
  return `Steps:\n${lines.join("\n")}`
}

function callHeader(step: number, action: string, where: string, outcome: string): string {
  return `[${step}] ${action} ${where || "/"} → ${outcome}`
}

function nextStep(w: CMSSearchWorker): number {
  const n = ((w as any).callCount ?? 0) + 1
  ;(w as any).callCount = n
  return n
}

/** One recorded call, as the node's trace panel renders it. */
export interface NavCall {
  step: number
  action: string
  path: string
  outcome: string
  failed: boolean
  /** Per-query detail: which table, how many rows, what failed. */
  steps: StepTrace[]
  result: string
  /** Set when the call searched by meaning as well as words. */
  meaning?: MeaningRun
  /** A search's results in rank order, marking which the agent received. */
  results?: RankedResult[]
}

// Flags a meaning search in flight on the worker, so the node can show it
const meaningIndicator = (w: CMSSearchWorker) => (active: boolean) => {
  (w as any).searchingByMeaning = active
  w.updateWorker?.()
}

const RESULT_KEEP = 20000

const CALL_LOG_LIMIT = 40

function logCall(w: CMSSearchWorker, call: NavCall) {
  const log: NavCall[] = ((w as any).callLog ??= [])
  log.push(call)
  if (log.length > CALL_LOG_LIMIT) log.splice(0, log.length - CALL_LOG_LIMIT)
  w.updateWorker?.()
}

// References from tool calls build up over one agent run and start fresh with the next,
// so the node's References output lists what the model actually read or found
export function addReferences(w: CMSSearchWorker, p: AgentParameters, refs: { link: string; title: string }[]) {
  const holder = w as any
  if (holder.referencesRun !== p) {
    holder.referencesRun = p
    w.fields.references.value = []
  }
  const current = (w.fields.references.value as { link: string; title: string }[] | undefined) ?? []
  const next = [...current]
  for (const r of refs) if (r.link && !next.some(c => c.link === r.link)) next.push(r)
  w.fields.references.value = next
}

// A read document's title: its first heading, else the last part of its path
function referenceTitle(body: string, path: string): string {
  return body.match(/^#{1,3}\s+(.+)$/m)?.[1]?.trim() || path.split("/").pop() || path
}

// ── Worker execution ─────────────────────────────────────────────────────────

async function execute(worker: CMSSearchWorker, p: AgentParameters) {
  worker.fields.output.value = []
  worker.fields.textOutput.value = ""
  worker.fields.references.value = []

  const query = String(worker.fields.input.value ?? "").trim()
  if (!query) return

  // In the editor a setup problem stops the run so it's seen. On a live channel the flow carries on
  // without the CMS, so the beneficiary still gets a reply
  const unavailable = (message: string) => {
    if (p.debug) throw new Error(message)
    worker.error = message
    console.warn(`[cmssearch] ${message}`)
  }
  const team = p.team
  if (!team) return unavailable(NO_TEAM_MESSAGE)
  const areas = enabledAreas(worker.parameters)
  if (!areas.length) return unavailable(NO_AREAS_MESSAGE)
  if (isEmptyCms(await contentPresence(team, areas), areas)) return unavailable(emptyCmsMessage(areas))

  const ctx: Ctx = {
    team,
    locale: worker.parameters.locale || worker.fields.locale?.value || undefined,
    // Honour the node's Max Results input handle, as locale's is.
    limit: Number(worker.fields.maxResults?.value) || worker.parameters.maxResults || 5,
    trace: [],
    openaiKey: p.apiKeys?.openai,
    searchMode: worker.parameters.searchMode,
    onMeaning: meaningIndicator(worker),
  }
  ctx.defaultLocale = ((worker as any).defaultLocale ??= await loadDefaultLocale(ctx))

  const hits = (await searchCms(ctx, areas, query, [])).sort((a, b) => b.score - a.score).slice(0, ctx.limit)

  worker.fields.output.value = hits
  worker.fields.textOutput.value = hits.length
    ? hits.map(h => `### ${h.title}\n_${h.path}_\n\n${h.body || "(no body text)"}`).join("\n\n---\n\n")
    : "No matching content."
  worker.fields.references.value = hits.map(h => ({ link: h.url ?? h.path, title: h.title }))

  logCall(worker, {
    step: nextStep(worker),
    action: "search",
    path: areas.join("+"),
    outcome: `${hits.length} match${hits.length === 1 ? "" : "es"} for "${query}"`,
    failed: ctx.trace.some(t => t.error),
    steps: ctx.trace,
    result: worker.fields.textOutput.value as string,
    meaning: ctx.meaning,
    results: ctx.ranked,
  })
}

// ── Tool surface ─────────────────────────────────────────────────────────────

const ACTIONS = ["list", "tree", "read", "search"] as const

// How to work the tool when search matches by meaning: search first, browse when it misses
const SEARCH_FIRST_GUIDE =
  `\n\nACTIONS`
  + `\n- search(query, path?)  THE DEFAULT MOVE. Finds content by meaning as well as words, in any language,`
  + ` so it finds "shelter" articles from "somewhere to sleep". path:"" searches the whole CMS; pass a path to search inside one branch.`
  + `\n- read(path)   full text of one document (an article, service, or page block).`
  + `\n- list(path)   children of one node. path:"" returns the areas.`
  + `\n- tree(path,depth)  several levels at once.`
  + `\n\nPATHS look like "articles/category:01H.../section:01H...". Never compose one`
  + ` by hand — every result gives you the exact path of each entry, so copy it.`
  + `\n\nHOW TO WORK`
  + `\n1. For a question about a topic, search first, with path:"". Don't list or tree the CMS to find content;`
  + ` search is faster and finds more. Search the topic in a few words ("housing eligibility", "legal help asylum"),`
  + ` not the whole question.`
  + `\n2. Read the most relevant results before answering: search returns an excerpt, read returns the whole document.`
  + ` Results marked "related by meaning" may not use the query's words; check they answer the question.`
  + `\n3. Use list or tree only when the user asks what exists ("which categories are there"), or to look around`
  + ` a branch a search result led you to.`
  + `\n4. No matches is not an answer yet. Retry once with different terms, then browse the area.`
  + ` Only after that is "it is not in the CMS" a finding.`

// How to work the tool when search matches words only: browse to learn the words, then search
const BROWSE_FIRST_GUIDE =
  `\n\nACTIONS`
  + `\n- list(path)   children of one node. path:"" returns the areas. THE DEFAULT MOVE.`
  + `\n- tree(path,depth)  several levels at once, when listing one by one is too slow.`
  + `\n- read(path)   full text of one document (an article, service, or page block).`
  + `\n- search(query, path?)  keyword match; pass a path to search only inside it.`
  + `\n\nPATHS look like "articles/category:01H.../section:01H...". Never compose one`
  + ` by hand — every result gives you the exact path of each entry, so copy it.`
  + `\n\nHOW TO WORK`
  + `\n1. Do not guess a query against content you have not seen. If you do not know`
  + ` what exists, list("") first: three calls down a tree beats one blind search.`
  + `\n2. Entries are marked [dir] (list it) or [doc] (read it), and [dir] entries show`
  + ` how many children they hold — an empty branch is visible as empty.`
  + `\n3. Chain calls. Narrow with list/tree, then read the specific documents, or`
  + ` search inside the branch you landed on. A vague result is a reason to go a`
  + ` level deeper, not a reason to stop.`
  + `\n4. Search the TOPIC, not the sentence. "What are the eligibility requirements`
  + ` for housing support?" searches well as "housing eligibility" and badly as the`
  + ` whole question — matching is on words that appear in the content, and a`
  + ` question's words mostly do not.`
  + `\n5. No matches is not an answer yet. Retry once with broader or different terms,`
  + ` or browse the area instead. Only after that is "it is not in the CMS" a finding.`

export function getTool(w: CMSSearchWorker, p: AgentParameters): ToolConfig {
  const toolName = "cms_navigator"

  const areas = enabledAreas(w.parameters)
  // Meaning search can run, so searching first finds content whatever words it uses
  const searchFirst = w.parameters.searchMode !== "keyword" && !!p.apiKeys?.openai

  return {
    name: toolName,
    description:
      (w.parameters?.toolDescription
        ?? "Navigate and read the team's CMS: knowledge-base articles, mapped services, and site page content.")
      + `\n\nThe CMS is a TREE, and this tool walks it like a filesystem. Available areas: `
      + `${areas.join(", ")}.`
      + (searchFirst ? SEARCH_FIRST_GUIDE : BROWSE_FIRST_GUIDE)
      + `\n\nCITING\nQuote from read() output. To link the reader to an article or service, use its url line as the`
      + ` link, e.g. [Title](/services/1234567890); a path is only for navigating this tool and isn't a web`
      + ` address. Records with no url have no public page, so don't link them. If a call reports`
      + ` INCOMPLETE, or you never retrieved the content, say what you could not verify`
      + ` rather than answering as though you had it. Never present an unverified claim`
      + ` as something the CMS says.`,
    parameters: z.object({
      action: z.enum(ACTIONS)
        .describe('Which operation to run: "list", "tree", "read" or "search".'),
      path: z.string()
        .describe(
          'Where to act. "" is the root (the list of areas). Copy a path from an earlier '
          + 'result, e.g. "articles/category:01HXYZ". For search, an optional scope.',
        ),
      query: z.string()
        .describe('Search terms. Only used when action is "search"; pass "" otherwise.'),
      depth: z.number().nullable()
        .describe(`Levels to expand for "tree" (1-${MAX_TREE_DEPTH}). Null means 2.`),
    }),

    async execute(args, ctx) {
      const { action, path, query, depth } = args

      // Configuration problems also go on the node's title bar, where they're visible
      w.error = null
      const refuse = (reason: string, detail: string, nodeError?: string) => {
        const outcome = `REFUSED — ${reason}`
        const step = nextStep(w)
        if (nodeError) w.error = nodeError
        logCall(w, { step, action, path, outcome, failed: true, steps: [], result: detail })
        return `${callHeader(step, action, path, outcome)}\n\n${detail}`
      }

      // What the model is told: the setup problem in the editor, a neutral note on a live channel
      const tell = (editorNote: string) => p.debug ? editorNote : LIVE_UNAVAILABLE

      const team = p.team
      if (!team) {
        return refuse(
          "no team",
          tell(NO_TEAM_MESSAGE + " Tell the user this; don't retry or try another path, the result will be the same."),
          NO_TEAM_MESSAGE,
        )
      }

      const allowed = enabledAreas(w.parameters)
      if (!allowed.length) return refuse("all areas off", tell(NO_AREAS_MESSAGE), NO_AREAS_MESSAGE)

      const has = await contentPresence(team, allowed)
      if (isEmptyCms(has, allowed)) {
        return refuse(
          "CMS is empty",
          tell(emptyCmsMessage(allowed) + " Tell the user the CMS has nothing to answer from yet; don't search further."),
          emptyCmsMessage(allowed),
        )
      }

      const parsed = parsePath(path)
      if ("error" in parsed) return refuse("bad path", parsed.error)
      // narrowing from the guard above does not reach into a nested closure.
      const { area: pathArea, segments } = parsed
      if (pathArea && !allowed.includes(pathArea)) {
        return refuse(
          `${pathArea} is off`,
          `The "${pathArea}" area is switched off on this node. Available: ${allowed.join(", ")}.`,
        )
      }

      const runCtx: Ctx = {
        team,
        locale: w.parameters.locale || undefined,
        limit: w.parameters.maxResults || 5,
        trace: [],
        openaiKey: p.apiKeys?.openai,
        searchMode: w.parameters.searchMode,
        onMeaning: meaningIndicator(w),
      }
      // Cached on the worker; it does not change between calls in a run.
      // a navigation walk is many calls.
      runCtx.defaultLocale = ((w as any).defaultLocale ??= await loadDefaultLocale(runCtx))

      const step = nextStep(w)
      const { body, outcome } = await run()

      const header = callHeader(step, action, path, outcome)
      const trace = formatTrace(runCtx.trace)

      const failedSteps = runCtx.trace.filter(t => t.error)
      const cappedSteps = runCtx.trace.filter(t => t.truncated)
      const warnings: string[] = []
      if (failedSteps.length) {
        warnings.push(
          `INCOMPLETE — ${failedSteps.length} of ${runCtx.trace.length} queries failed `
          + `(${failedSteps.map(t => t.label).join(", ")}). What follows is missing whatever `
          + `those would have returned. Say so rather than presenting this as the full answer.`,
        )
      }
      // count shown for a capped listing is a floor, not a total.
      if (cappedSteps.length) {
        warnings.push(
          `CAPPED — ${cappedSteps.map(t => t.label).join(", ")} hit the ${FETCH_CEILING}-row `
          + `limit, so this is a partial view and any count is a lower bound. Narrow the path `
          + `or search within a branch rather than treating this as everything there is.`,
        )
      }
      const warning = warnings.join("\n")

      const out = [header, warning, "", body, trace && `\n${trace}`]
        .filter(Boolean).join("\n")

      // Surfaced on the node itself, so the canvas shows the trail too.
      logCall(w, {
        step, action, path, outcome,
        failed: outcome.startsWith("FAILED") || runCtx.trace.some(t => t.error),
        steps: runCtx.trace,
        meaning: runCtx.meaning,
        results: runCtx.ranked,
        result: body.length > RESULT_KEEP ? `${body.slice(0, RESULT_KEEP)}

…truncated` : body,
      })
      ctx["searchResults"] = out
      return out

      async function run(): Promise<{ body: string; outcome: string }> {
        // Outcomes are phrased as counts, so an empty result is unmistakably
        const plural = (n: number, one: string, many = `${one}s`) =>
          `${n} ${n === 1 ? one : many}`

        try {
          // Root: the areas themselves, so an agent with no context has a
          // first call that always works.
          if (!pathArea) {
            if (action === "read") {
              return {
                body: 'The root is not a document. Call list with path:"" instead.',
                outcome: "not a document",
              }
            }
            if (action === "search") {
              const hits = await searchCms(runCtx, allowed, query, [])
              addReferences(w, p, [...hits].sort((a, b) => b.score - a.score).slice(0, runCtx.limit).map(h => ({ link: h.url ?? h.path, title: h.title })))
              return {
                body: formatSearch(hits, query, "the whole CMS", runCtx.limit),
                outcome: `${plural(Math.min(hits.length, runCtx.limit), "match", "matches")} for "${query}" across all areas`,
              }
            }
            return {
              body: formatEntries(
                allowed.map(a => ({
                  path: a,
                  kind: "area",
                  title: AREA_LABEL[a],
                  detail: has[a] === false ? `EMPTY: no content for this team yet` : areaHint(a),
                })),
                "CMS root — three areas:",
              ),
              outcome: `${plural(allowed.length, "area")} to explore`,
            }
          }

          const area = pathArea

          if (has[area] === false) {
            return {
              body: `The ${AREA_LABEL[area]} area has no content for this team yet. Try another area, or tell the user.`,
              outcome: "EMPTY — no content",
            }
          }

          if (action === "read") {
            const last = segments[segments.length - 1]
            if (!last) {
              return {
                body: `"${area}" is an area, not a document. Call list on it instead.`,
                outcome: "not a document",
              }
            }
            const body = await readNode(runCtx, area, last)
            // A missing or empty record isn't something the answer can cite
            if (!/^(No |Article \S+ has no translations)/.test(body)) {
              addReferences(w, p, [{ link: body.match(/^url: (\S+)$/m)?.[1] ?? path, title: referenceTitle(body, path) }])
            }
            return { body, outcome: `${last.kind}, ${body.length} chars` }
          }

          if (action === "search") {
            if (!query.trim()) {
              return {
                body: 'search needs a query. Pass one, or use action "list" to browse.',
                outcome: "no query given",
              }
            }
            const hits = await searchCms(runCtx, [area], query, segments)
            const shown = Math.min(hits.length, runCtx.limit)
            addReferences(w, p, [...hits].sort((a, b) => b.score - a.score).slice(0, runCtx.limit).map(h => ({ link: h.url ?? h.path, title: h.title })))
            return {
              body: formatSearch(hits, query, path || AREA_LABEL[area], runCtx.limit),
              outcome: `${plural(shown, "match", "matches")} for "${query}"`,
            }
          }

          if (action === "tree") {
            const d = Math.max(1, Math.min(depth ?? 2, MAX_TREE_DEPTH))
            const lines = await buildTree(runCtx, area, segments, d)
            const head = `${path || AREA_LABEL[area]} — ${d} level${d === 1 ? "" : "s"} deep:`
            return {
              body: lines.length ? `${head}\n\n${lines.join("\n")}` : `${head}\n\n(empty)`,
              outcome: `${plural(lines.length, "node")}, ${d} level${d === 1 ? "" : "s"} deep`,
            }
          }

          const entries = await listArea(runCtx, area, segments)
          // Split by type: "3 dirs, 12 docs" tells the reader whether to keep
          // descending or start reading, which is the decision at every step.
          const docs = entries.filter(e => e.readable).length
          const dirs = entries.length - docs
          const parts = [dirs && `${plural(dirs, "folder")}`, docs && `${plural(docs, "document")}`]
            .filter(Boolean) as string[]
          return {
            body: formatEntries(entries, `${path || AREA_LABEL[area]}:`),
            outcome: parts.length ? parts.join(", ") : "empty",
          }
        } catch (err) {
          return { body: `CMS Navigator failed: ${errText(err)}`, outcome: `FAILED — ${errText(err)}` }
        }
      }
    },
  }
}

const areaHint = (a: Area) =>
  a === "articles" ? "knowledge base: category > section > article"
    : a === "services"
      ? "services three ways: by geography (country > region > city), by category, or by provider"
      : "site settings and page-content blocks"

// One search result as the agent receives it: title, details, path and url, then the full text
function formatHit(h: Hit): string {
  // Meaning-only matches may not contain the query's words; the model should know
  const meta = [
    h.kind, h.detail,
    h.matched === "meaning" && "related by meaning",
    h.similarity !== undefined && `similarity ${h.similarity.toFixed(2)}`,
  ].filter(Boolean).join(" · ")
  return `### ${h.title}\n_${meta}_\npath: ${h.path}${h.url ? `\nurl: ${h.url}` : ""}\n\n${h.body || "(no body text)"}`
}

function formatSearch(hits: Hit[], query: string, where: string, limit: number): string {
  const top = [...hits].sort((a, b) => b.score - a.score).slice(0, limit)
  if (!top.length) {
    return `No matches for "${query}" in ${where}.\n\n`
      + `Keyword search only finds words that actually appear in the content. If you are `
      + `not sure what is there, browse instead: list this path and look at what exists.`
  }
  const body = top.map(formatHit).join("\n\n---\n\n")
  return `${top.length} match${top.length === 1 ? "" : "es"} for "${query}" in ${where}:\n\n${body}`
}

export const cmssearch: WorkerRegistryItem = {
  title: "CMS Navigator 🧪",
  category: "tool",
  type: "cmssearch",
  description:
    "Navigate the Smart CMS as a tree — list categories and sections, read articles, "
    + "services and page content, and search within any branch.",
  execute,
  create(agent: Agent) {
    const w = agent.initializeWorker(
      { type: "cmssearch", parameters: { maxResults: 5 } },
      [
        { type: "string", direction: "input", title: "Query", name: "input" },
        { type: "doc", direction: "output", title: "Results", name: "output" },
        { type: "string", direction: "output", title: "Text Output", name: "textOutput" },
        { type: "references", direction: "output", title: "References", name: "references" },
        { type: "tool", direction: "input", title: "Tool", name: "tool" },
        { type: "string", direction: "input", title: "Locale", name: "locale" },
        { type: "number", direction: "input", title: "Max Results", name: "maxResults" },
      ],
      cmssearch,
    )
    w.getTool = getTool
    return w
  },
  get registry() { return cmssearch },
}
